
import { Routes, Route } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import LeadDndPipeline from "@/components/leads/LeadDndPipeline";
import { AppErrorBoundary } from "@/components/error-handling/AppErrorBoundary";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LeadsDashboard from "@/components/leads/LeadsDashboard";
import { LeadsEmptyState } from "@/components/empty-states/LeadsEmptyState";
import { useOptimizedLeads, useLeadsCount } from "@/hooks/useOptimizedLeads";
import { useMemoryCleanup } from "@/utils/memory/memoryManager";
import LeadDetail from "./LeadDetail";
import LeadNew from "./LeadNew";

const LeadsList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("pipeline");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Use optimized leads hook with pagination
  const { data: leads = [], isLoading } = useOptimizedLeads({
    page: currentPage,
    pageSize
  });
  
  const { data: totalCount = 0 } = useLeadsCount();

  // Memory cleanup for component
  useMemoryCleanup(() => {
    // Cleanup any retained references
    setCurrentPage(1);
  });

  // Memoize expensive calculations
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / pageSize);
  }, [totalCount, pageSize]);

  const handleAddLead = () => {
    navigate('/leads/new');
  };

  const handleSearchJobs = () => {
    navigate('/leads?tab=search');
  };

  if (!isLoading && leads.length === 0 && currentPage === 1) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Business Development</h1>
            <p className="text-muted-foreground mt-1">
              Track sales pipeline, manage leads, and monitor business development activities.
            </p>
          </div>
        </div>
        
        <Separator />
        
        <LeadsEmptyState onAddLead={handleAddLead} onSearchJobs={handleSearchJobs} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Business Development</h1>
          <p className="text-muted-foreground mt-1">
            Track sales pipeline, manage leads, and monitor business development activities.
          </p>
        </div>
        <Button onClick={handleAddLead}>
          <Plus className="mr-2 h-4 w-4" /> Add Lead
        </Button>
      </div>
      
      <Separator />
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
        </TabsList>
        
        <TabsContent value="pipeline" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Lead Pipeline</CardTitle>
              {totalCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  Showing {leads.length} of {totalCount} leads
                </p>
              )}
            </CardHeader>
            <CardContent>
              <AppErrorBoundary componentName="LeadDndPipeline">
                <LeadDndPipeline />
              </AppErrorBoundary>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="dashboard" className="mt-6">
          <AppErrorBoundary componentName="LeadsDashboard">
            <LeadsDashboard />
          </AppErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Leads = () => {
  return (
    <Routes>
      <Route path="/" element={<LeadsList />} />
      <Route path="/new" element={<LeadNew />} />
      <Route path="/:id" element={<LeadDetail />} />
    </Routes>
  );
};

export default Leads;
